
import { useState } from 'react';

interface DebtCalculatorWidgetProps {
  className?: string;
}

export default function DebtCalculatorWidget({ className = '' }: DebtCalculatorWidgetProps) {
  const [totalDebt, setTotalDebt] = useState('');
  const [monthlyIncome, setMonthlyIncome] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState({
    potentialSavings: 0,
    newMonthlyPayment: 0,
    timeToFreedom: 0
  });

  const calculateSavings = () => {
    const debt = parseFloat(totalDebt);
    const income = parseFloat(monthlyIncome);
    
    if (debt && income && debt > 0 && income > 0) {
      const savingsPercentage = Math.min(Math.max(0.4, debt / 100000), 0.6);
      const potentialSavings = debt * savingsPercentage;
      const remainingDebt = debt - potentialSavings;
      const newMonthlyPayment = Math.min(income * 0.15, remainingDebt / 36);
      const timeToFreedom = remainingDebt / newMonthlyPayment / 12;
      
      setResults({
        potentialSavings: Math.round(potentialSavings),
        newMonthlyPayment: Math.round(newMonthlyPayment),
        timeToFreedom: Math.round(timeToFreedom * 10) / 10
      });
      setShowResults(true);
    }
  };

  const handleGetStarted = () => {
    // Trigger Readdy Agent widget
    const widget = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
    if (widget) {
      widget.click();
    }
  };

  return (
    <div className={`bg-white rounded-lg shadow-xl p-6 ${className}`}>
      <div className="text-center mb-6">
        <i className="fas fa-calculator text-3xl text-teal-600 mb-3"></i>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Free Debt Savings Calculator</h3>
        <p className="text-gray-600">See how much you could save with our debt settlement program</p>
      </div>

      {!showResults ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Total Unsecured Debt
            </label>
            <div className="relative">
              <span className="absolute left-3 top-3 text-gray-500">$</span>
              <input
                type="number"
                value={totalDebt}
                onChange={(e) => setTotalDebt(e.target.value)}
                className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="50,000"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Monthly Income
            </label>
            <div className="relative">
              <span className="absolute left-3 top-3 text-gray-500">$</span>
              <input
                type="number"
                value={monthlyIncome}
                onChange={(e) => setMonthlyIncome(e.target.value)}
                className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="4,000"
              />
            </div>
          </div>

          <button
            onClick={calculateSavings}
            disabled={!totalDebt || !monthlyIncome}
            className="w-full bg-teal-600 text-white py-3 rounded-md font-semibold hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
          >
            Calculate My Savings
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                ${results.potentialSavings.toLocaleString()}
              </div>
              <div className="text-sm text-green-700">Potential Savings</div>
            </div>
            
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                ${results.newMonthlyPayment.toLocaleString()}
              </div>
              <div className="text-sm text-blue-700">New Monthly Payment</div>
            </div>
            
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">
                {results.timeToFreedom} years
              </div>
              <div className="text-sm text-purple-700">Time to Freedom</div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <i className="fas fa-lightbulb text-yellow-600 mr-3 mt-1"></i>
              <div>
                <h4 className="font-semibold text-yellow-800 mb-1">Great News!</h4>
                <p className="text-sm text-yellow-700">
                  Based on your debt amount, you could potentially save ${results.potentialSavings.toLocaleString()} 
                  through our debt settlement program. These are estimated savings - your actual results may vary.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <button
              onClick={handleGetStarted}
              className="w-full bg-teal-600 text-white py-3 rounded-md font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap"
            >
              Get My Free Consultation
            </button>
            
            <button
              onClick={() => {setShowResults(false); setTotalDebt(''); setMonthlyIncome('');}}
              className="w-full bg-gray-100 text-gray-700 py-2 rounded-md font-medium hover:bg-gray-200 transition-colors whitespace-nowrap"
            >
              Calculate Again
            </button>
          </div>
        </div>
      )}

      <div className="mt-4 text-xs text-gray-500 text-center">
        *Results are estimates based on industry averages. Actual savings may vary.
      </div>
    </div>
  );
}
